import React from 'react';
import { Avatar, Box, Typography } from '@mui/material';
import { getAnnouncementImageUrl } from '../../utils/imageUrl';
import { splitFrenchDateTime } from '../../utils/formatDateTimeFr';
import { isDeliveryOfferMessage } from '../../utils/deliveryOfferMessage';
import { isDeliveryPaymentMessage } from '../../utils/colissimoPaymentMessage';
import { isDeliveryRestitutionElsewhereMessage } from '../../utils/deliveryRestitutionElsewhereMessage';
import DeliveryOfferMessageBody from '../DeliveryOfferMessageBody';
import DeliveryPaymentReceivedMessageBody from '../DeliveryPaymentReceivedMessageBody';
import type { DeliveryPricingResponse } from '../../utils/deliveryPricing';
import {
  ConversationTheme,
  conversationListItemSx,
  getConversationTheme,
} from '../../utils/conversationTheme';
import {
  getAppleMessageBubbleSx,
  getMessageGroupPosition,
  getMessageGroupSpacing,
} from '../../utils/messageBubbleShape';

/** Même convention que sherlook-frontend-next (messages). */
export const IMAGE_MESSAGE_PREFIX = '[[image]]';

/** Fond du fil de discussion (sert aussi à masquer/galber la pointe des bulles). */
export const CONVERSATION_CHAT_BACKGROUND = '#F8F9FB';

function parseImageMessage(content: string): { imagePath: string; caption: string } | null {
  if (!content || typeof content !== 'string' || !content.startsWith(IMAGE_MESSAGE_PREFIX)) return null;
  const payload = content.slice(IMAGE_MESSAGE_PREFIX.length);
  const [rawPath, ...rest] = payload.split('\n');
  const imagePath = rawPath.trim();
  if (!imagePath) return null;
  const caption = rest.join('\n').trim();
  return { imagePath, caption };
}

function formatMessageSenderName(
  sender?: { first_name?: string | null; last_name?: string | null } | null
): string {
  const full = [sender?.first_name, sender?.last_name].filter(Boolean).join(' ').trim();
  return full || 'Le propriétaire';
}

function ChatMessageBody({
  content,
  outgoing,
  sender,
  deliveryPricing,
  deliveryLocationProviderId,
}: {
  content: string;
  outgoing?: boolean;
  sender?: { first_name?: string | null; last_name?: string | null } | null;
  deliveryPricing?: DeliveryPricingResponse | null;
  deliveryLocationProviderId?: number;
}) {
  const parsed = parseImageMessage(content);
  const captionColor = outgoing ? 'rgba(255,255,255,0.95)' : undefined;
  if (parsed) {
    const src = getAnnouncementImageUrl(parsed.imagePath);
    return (
      <>
        {parsed.caption ? (
          <Typography
            component="div"
            variant="body2"
            sx={{ whiteSpace: 'pre-wrap', mb: 0.75, color: captionColor }}
          >
            {parsed.caption}
          </Typography>
        ) : null}
        <Box
          component="img"
          src={src}
          alt={parsed.caption || 'Image'}
          sx={{
            maxWidth: '100%',
            maxHeight: 240,
            borderRadius: 1,
            display: 'block',
            objectFit: 'cover',
            bgcolor: outgoing ? 'rgba(255,255,255,0.12)' : 'action.hover',
          }}
        />
      </>
    );
  }
  if (isDeliveryOfferMessage(content)) {
    return (
      <DeliveryOfferMessageBody
        content={content}
        outgoing={outgoing}
        pricing={deliveryPricing}
        locationProviderId={deliveryLocationProviderId}
      />
    );
  }
  if (isDeliveryPaymentMessage(content)) {
    return (
      <DeliveryPaymentReceivedMessageBody
        content={content}
        payerName={formatMessageSenderName(sender)}
        outgoing={outgoing}
      />
    );
  }
  if (isDeliveryRestitutionElsewhereMessage(content)) {
    return (
      <Typography
        variant="body2"
        sx={{
          whiteSpace: 'pre-wrap',
          lineHeight: 1.45,
          fontStyle: 'italic',
          ...(outgoing ? { color: 'rgba(255,255,255,0.95)' } : {}),
        }}
      >
        Sherlook — Cet objet a été restitué à son propriétaire.
      </Typography>
    );
  }
  return (
    <Typography
      variant="body2"
      sx={{
        whiteSpace: 'pre-wrap',
        lineHeight: 1.45,
        ...(outgoing ? { color: 'rgba(255,255,255,0.95)' } : {}),
      }}
    >
      {content}
    </Typography>
  );
}

function formatMessageTime(createdAt?: string | null): string {
  if (!createdAt) return '';
  // L'API renvoie « Y-m-d H:i:s » sans offset, déjà en heure serveur (Europe/Paris).
  // Forcer « Z » (UTC) provoquait un décalage +2h en été.
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}/.test(createdAt);
  if (!hasZone) {
    const wallClock = createdAt.match(/(?:^|T|\s)(\d{2}):(\d{2})(?::\d{2})?/);
    if (wallClock) {
      return `${wallClock[1]}:${wallClock[2]}`;
    }
  }
  const normalized = createdAt.includes('T') ? createdAt : createdAt.replace(' ', 'T');
  const d = new Date(normalized);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  }
  return splitFrenchDateTime(createdAt)?.time ?? createdAt;
}

type ConversationMessage = {
  id: number;
  content: string;
  created_at?: string | null;
  is_from_me?: boolean;
  sender?: {
    first_name?: string | null;
    last_name?: string | null;
  } | null;
};

export function ConversationMessageBubble({
  message,
  theme,
  groupPosition,
  deliveryPricing,
  deliveryLocationProviderId,
}: {
  message: ConversationMessage;
  theme: ConversationTheme;
  groupPosition: ReturnType<typeof getMessageGroupPosition>;
  deliveryPricing?: DeliveryPricingResponse | null;
  deliveryLocationProviderId?: number;
}) {
  const outgoing = !!message.is_from_me;
  const timeLabel = formatMessageTime(message.created_at);
  const bubbleBg = outgoing ? theme.bubbleOutgoing : theme.bubbleIncoming;
  const showTimestamp = groupPosition === 'single' || groupPosition === 'last';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: outgoing ? 'flex-end' : 'flex-start',
        mb: getMessageGroupSpacing(groupPosition),
        width: '100%',
      }}
    >
      <Box
        sx={{
          maxWidth: { xs: '88%', sm: '72%' },
          px: 1.75,
          py: 1,
          mx: outgoing ? 1 : 1.25,
          color: outgoing ? theme.bubbleOutgoingText : theme.bubbleIncomingText,
          boxShadow: outgoing ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
          overflow: 'visible',
          ...getAppleMessageBubbleSx(outgoing, bubbleBg, groupPosition, CONVERSATION_CHAT_BACKGROUND),
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <ChatMessageBody
            content={message.content}
            outgoing={outgoing}
            sender={message.sender}
            deliveryPricing={deliveryPricing}
            deliveryLocationProviderId={deliveryLocationProviderId}
          />
        </Box>
      </Box>
      {showTimestamp && timeLabel ? (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            mt: 0.5,
            px: 1,
            fontSize: '0.72rem',
          }}
        >
          {timeLabel}
        </Typography>
      ) : null}
    </Box>
  );
}

export function ConversationMessageList({
  messages,
  theme,
  deliveryPricing,
  deliveryLocationProviderId,
}: {
  messages: ConversationMessage[];
  theme: ConversationTheme;
  deliveryPricing?: DeliveryPricingResponse | null;
  deliveryLocationProviderId?: number;
}) {
  return (
    <>
      {messages.map((message, index) => (
        <ConversationMessageBubble
          key={message.id}
          message={message}
          theme={theme}
          groupPosition={getMessageGroupPosition(messages, index)}
          deliveryPricing={deliveryPricing}
          deliveryLocationProviderId={deliveryLocationProviderId}
        />
      ))}
    </>
  );
}

export function ConversationAvatar({
  conversationId,
  imageSrc,
  initial,
}: {
  conversationId: number;
  imageSrc?: string | null;
  initial?: string | null;
}) {
  const theme = getConversationTheme(conversationId);
  const letter = (initial || '?').trim().charAt(0).toUpperCase() || '?';

  return (
    <Avatar
      src={imageSrc || undefined}
      sx={{
        bgcolor: theme.avatarBg,
        color: theme.avatarText,
        fontWeight: 700,
        ...(imageSrc
          ? {
              border: `2px solid ${theme.accent}`,
            }
          : {}),
      }}
    >
      {letter}
    </Avatar>
  );
}

export function getConversationListButtonSx(conversationId: number, selected: boolean) {
  const theme = getConversationTheme(conversationId);
  return {
    ...(conversationListItemSx(theme, selected) ?? {}),
    '&.Mui-selected': {
      ...conversationListItemSx(theme, true),
    },
  };
}
